import React from 'react'
import { Alert, Button, Form, Input, Select, Switch } from 'antd'
import { passwordIconRender, validateStrongPassword, PasswordRequirements, validatePasswordMatch } from '../../../../utils/antd.helper'
import { UserRoles } from '../../../../constants'
import useAuthorization from '../../../../components/authorization/useAuthorization'
import UserStatus from '../../../../constants/UserStatus'
import { useIsSenseUserOrImpersonated } from '../../../../hooks/useIsSenseUserOrImpersonated'
import { useSelector } from 'react-redux'
import userSelectors from '../../../../store/selectors/user.selectors'
import { 
    getRoleOptions, 
    separateVisibleAndHiddenRoles, 
    mergeVisibleAndHiddenRoles 
} from '../../../../utils/rolePermissions.helper'
import { SENSE_ORGANIZATION_ID } from '../../../../constants/UserRoles'

const UserForm = ({
    organizations,
    isFetchingOrganizations,
    initialValues,
    onSubmit,
    loading,
    error,
    fieldsToHide = [],
    actionName = 'Submit',
}) => {
    const [form] = Form.useForm()
    const email = Form.useWatch('email', form) || initialValues?.email
    const isTargetUserHasSenseDomain = email?.endsWith('@sense.network') || initialValues?.email?.endsWith('@sense.network')
    const password = Form.useWatch('password', form)
    
    const currentUserOrganizationId = useSelector(userSelectors.organizationId)
    const isSenseUserOrImpersonated = useIsSenseUserOrImpersonated()

    const { isAuthorized: isOrgAdmin } = useAuthorization([UserRoles.ADMIN])
    const { isAuthorized: isOrgUserManager } = useAuthorization([UserRoles.USER_MANAGER])
    const isOrgAdminNonSense = (isOrgAdmin || isOrgUserManager) && currentUserOrganizationId !== SENSE_ORGANIZATION_ID

    const organizationOptions = organizations?.map(organization => ({
        value: organization.id,
        label: organization.name,
    }))

    // Get role options using the helper
    const isCreateForm = !fieldsToHide.includes('password')
    const roleOptions = getRoleOptions(isSenseUserOrImpersonated, isTargetUserHasSenseDomain, isCreateForm)

    // Separate visible and hidden roles for editing
    const { visibleRoles, hiddenRoles } = separateVisibleAndHiddenRoles(initialValues?.roles, roleOptions)


    // Convert status to boolean for the switch
    const handleFormSubmit = (values) => {
        // If there's a status field and it's a boolean, convert it to proper UserStatus enum
        if (values.status !== undefined && typeof values.status === 'boolean') {
            values.status = values.status ? UserStatus.ACTIVE : UserStatus.DISABLED;
        }
        
        // Merge visible roles with hidden roles to preserve roles set by System Admin
        if (values.roles && hiddenRoles.length > 0) {
            values.roles = mergeVisibleAndHiddenRoles(values.roles, hiddenRoles);
        }
        
        // If user is org admin but not Sense user/impersonated, auto-set user's organization to admin's organization
        if (isOrgAdminNonSense) {
            values.organizationId = currentUserOrganizationId;
            
            // Set role to REGULAR if no roles specified
            if (!values.roles || values.roles.length === 0) {
                values.roles = [UserRoles.REGULAR];
            }
        }
        
        onSubmit(values);
    };

    // If user is org admin but not Sense user/impersonated, hide organization field
    if (isOrgAdminNonSense && !fieldsToHide.includes('organizationId')) {
        fieldsToHide = [...fieldsToHide, 'organizationId']
    }

    return (
        <Form
            form={form}
            name='userForm'
            onFinish={handleFormSubmit}
            layout='vertical'
            requiredMark={false}
            validateTrigger={['onBlur', 'onSubmit']}
        >
            {!fieldsToHide.includes('email') && (
                <Form.Item
                    label='Email'
                    name='email'
                    rules={[
                        { required: true, message: "Please, input the email" },
                        { type: 'email', message: 'Please, input valid email' },
                    ]}
                    initialValue={initialValues?.email}
                >
                    <Input />
                </Form.Item>
            )}
            {!fieldsToHide.includes('name') && (
                <Form.Item label='Name' name='name' initialValue={initialValues?.name} rules={[{ required: true, message: "Please, input the name" }]}>
                    <Input />
                </Form.Item>
            )}
            {!fieldsToHide.includes('password') && (
                <>
                    <Form.Item
                        label='Password'
                        name='password'
                        rules={[
                            { required: true, message: "Please, input the password" },
                            { validator: validateStrongPassword },
                        ]}
                        extra={<PasswordRequirements value={password} />}
                    >
                        <Input.Password iconRender={passwordIconRender} />
                    </Form.Item>
                    <Form.Item
                        label='Confirm Password'
                        name='confirmPassword'
                        dependencies={['password']}
                        hasFeedback
                        rules={[
                            { required: true, message: "Please, confirm the password" },
                            validatePasswordMatch,
                        ]}
                    >
                        <Input.Password iconRender={passwordIconRender} />
                    </Form.Item>
                </>
            )}
            {!fieldsToHide.includes('organizationId') && (
                <Form.Item
                    label='Organization'
                    name='organizationId'
                    dependencies={['email']}
                    rules={[
                        { required: true, message: 'Please, select organization for the user' },
                        {
                            validator: (_, organizationId) => {
                                // Only users with @sense.network emails can be assigned to Sense organization
                                if (organizationId === SENSE_ORGANIZATION_ID) {
                                    if (!email?.endsWith('@sense.network')) {
                                        return Promise.reject(new Error('Only users with @sense.network emails can be assigned to the Sense organization'))
                                    }
                                }
                                return Promise.resolve()
                            }
                        }
                    ]}
                    initialValue={initialValues?.organizationId || (isOrgAdminNonSense ? currentUserOrganizationId : undefined)}
                >
                    <Select
                        placeholder='Please, select organization for the user'
                        notFoundContent='There are no organizations'
                        loading={isFetchingOrganizations}
                        options={organizationOptions}
                        showSearch
                        optionFilterProp='label'
                        popupMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                    />
                </Form.Item>
            )}
            {!fieldsToHide.includes('roles') && (
                <Form.Item label='Roles' name='roles' initialValue={visibleRoles.length > 0 ? visibleRoles : [UserRoles.REGULAR]}>
                    <Select
                        options={roleOptions}
                        optionType='button'
                        mode='multiple'
                        popupMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        showSearch
                        optionFilterProp='label'
                    />
                </Form.Item>
            )}
            {!fieldsToHide.includes('status') && (
                <Form.Item 
                    label='Active' 
                    name='status'
                    valuePropName='checked'
                    initialValue={initialValues?.status === UserStatus.ACTIVE}
                >
                    <Switch />
                </Form.Item>
            )}
            {error && <Alert message={error} type='error' showIcon closable />}
            <Form.Item>
                <Button type='primary' htmlType='submit' block loading={loading}>
                    {actionName}
                </Button>
            </Form.Item>
        </Form>
    )
}

export default UserForm
